/**
 * AJ 2026-10-01: "pages going white when you click through quickly."
 *
 * THE CAUSE THIS COVERS. Each report is its own lazily loaded chunk
 * (reports/index.tsx) with a hashed file name. A deploy replaces every chunk
 * name; a browser tab opened before the deploy still holds the old names and
 * asks for them on the first visit to each report. Cloudflare Pages answers a
 * missing asset with the app's index.html (HTTP 200, text/html), the browser
 * refuses it as a module ("Failed to fetch dynamically imported module", or
 * Vite's "Unable to preload CSS" for the stylesheet), and with nothing to
 * catch the rejected import React unmounted the whole app: a white page until
 * the reader reloaded. Production was deployed about five times on
 * 2026-10-01.
 *
 * THE RECOVERY. A failed chunk load reloads the page ONCE, by itself: the
 * reload fetches the current index.html, whose chunk names exist. The guard
 * against a reload loop is a stamp in sessionStorage (it survives the
 * reload): while a stamp younger than CHUNK_RELOAD_WINDOW_MS is there, a
 * second failure is not answered with another reload; the error goes to the
 * error boundary, which says so and offers Retry. So there is at most one
 * automatic reload per window, whatever else loads or fails meanwhile. The
 * stamp is never cleared on a success: with chunks prefetched in the
 * background, "another chunk loaded" would lift the guard while the broken
 * one was still failing, and that is a loop. A later deploy is past the
 * window and gets its own reload.
 *
 * Plain .mjs with no DOM access of its own (storage, clock and reload are
 * passed in), so node:test drives every branch
 * (scripts/chunk-recovery.test.mjs).
 */

/** sessionStorage key of the last automatic reload (epoch ms). */
export const CHUNK_RELOAD_KEY = "meridian:chunk-reload-at";

/** No second automatic reload within this long of the first. */
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60_000;

/**
 * Is this the browser failing to LOAD a chunk (missing file, wrong MIME type,
 * network), as opposed to an error thrown by code inside a chunk that loaded?
 * Only the first is cured by a reload.
 *
 *   Chrome / Edge   "Failed to fetch dynamically imported module: <url>"
 *   Firefox         "error loading dynamically imported module: <url>"
 *   Safari          "Importing a module script failed."
 *   Vite preload    "Unable to preload CSS for <path>"
 *   webpack-style   name "ChunkLoadError" (kept for a future bundler change)
 */
export function isChunkLoadError(err) {
  if (err === null || err === undefined) return false;
  const name = typeof err === "object" && typeof err.name === "string" ? err.name : "";
  if (name === "ChunkLoadError") return true;
  const message =
    typeof err === "string"
      ? err
      : typeof err === "object" && typeof err.message === "string"
        ? err.message
        : "";
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
  );
}

/**
 * The stamp of the last automatic reload: a number (epoch ms), null when
 * there is none, undefined when storage cannot be read at all (private mode,
 * disabled storage). undefined means "cannot guard", which callers treat as
 * "do not reload": no guard, no automatic reload.
 */
export function readReloadStamp(storage) {
  try {
    if (!storage || typeof storage.getItem !== "function") return undefined;
    const raw = storage.getItem(CHUNK_RELOAD_KEY);
    if (raw === null || raw === undefined || raw === "") return null;
    const at = Number(raw);
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return undefined;
  }
}

/**
 * May a failed chunk load be answered with an automatic reload right now?
 *
 *   lastReloadAt undefined  storage unusable: no (a reload could not be guarded)
 *   lastReloadAt null       never reloaded: yes
 *   within the window       already reloaded and it failed again: no
 *   older than the window   yes (a later deploy)
 *
 * A stamp from the future (clock change) counts as "within the window".
 */
export function shouldAutoReload(now, lastReloadAt, windowMs = CHUNK_RELOAD_WINDOW_MS) {
  if (lastReloadAt === undefined) return false;
  if (lastReloadAt === null) return true;
  return now - lastReloadAt >= windowMs;
}

/**
 * Answer a failed chunk load. Returns true when a reload was started (the
 * caller then keeps its loading state up; the page is about to go away) and
 * false when the error must be shown instead.
 *
 * The stamp is written BEFORE reload() is called, and a write that fails
 * means no reload: an unguarded reload is how a loop starts.
 *
 * @param {unknown} err
 * @param {{ storage?: Storage | null, now?: () => number, reload?: () => void, windowMs?: number }} [env]
 */
export function recoverFromChunkError(err, env = {}) {
  if (!isChunkLoadError(err)) return false;
  const { storage, reload } = env;
  const now = typeof env.now === "function" ? env.now() : Date.now();
  if (typeof reload !== "function") return false;
  if (!shouldAutoReload(now, readReloadStamp(storage), env.windowMs)) return false;
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
    // Read back: a storage that silently drops writes cannot guard the reload.
    if (Number(storage.getItem(CHUNK_RELOAD_KEY)) !== now) return false;
  } catch {
    return false;
  }
  reload();
  return true;
}

/**
 * The module loader behind every lazily loaded report.
 *
 *   load(id)      the module promise, shared: one request per chunk however
 *                 many callers ask (the route, a hover prefetch, the idle
 *                 prefetch).
 *   prefetch(id)  start the load and swallow a failure: a prefetch must never
 *                 reload the page or raise an error by itself. The click that
 *                 follows goes through forRoute() and is handled there.
 *   forRoute(id)  load() for a route the reader is opening. A chunk load
 *                 failure is answered with the one automatic reload; when a
 *                 reload starts, the promise never settles, so the loading
 *                 state stays up until the page goes away instead of an error
 *                 card flashing first. Otherwise the error is rethrown for
 *                 the error boundary.
 *
 * A CHUNK THAT FAILED TO LOAD IS NEVER IMPORTED AGAIN IN THE SAME DOCUMENT.
 * Its failure is kept, and every later ask for it gets the same failure, so
 * the only way forward is a reload (the automatic one, or the card's button).
 * The reason is the stylesheet: Vite's preload helper asks for a chunk's CSS
 * once per document, so a second import after a failed first one would load
 * the code without its styles and draw the report unstyled (seen in the
 * local proof, 2026-10-01, with the Revenue stylesheet blocked). A failure
 * that is not a load failure (the chunk's own code threw while starting) is
 * forgotten, so a Retry runs it again.
 *
 * @param {(id: string) => (Promise<unknown> | null)} importer  the dynamic import for an id, or null when there is none
 * @param {{ storage?: Storage | null, now?: () => number, reload?: () => void, windowMs?: number, warn?: (m: string) => void }} [env]
 */
export function createChunkLoader(importer, env = {}) {
  const pending = new Map();
  const warn = typeof env.warn === "function" ? env.warn : () => {};

  function load(id) {
    const hit = pending.get(id);
    if (hit) return hit;
    let started;
    try {
      started = importer(id);
    } catch (err) {
      return Promise.reject(err);
    }
    if (!started) return Promise.reject(new Error(`no chunk registered for "${id}"`));
    const promise = Promise.resolve(started).then(undefined, (err) => {
      // A load failure stays (see above); anything else may be tried again.
      if (!isChunkLoadError(err) && pending.get(id) === promise) pending.delete(id);
      throw err;
    });
    pending.set(id, promise);
    return promise;
  }

  function prefetch(id) {
    try {
      load(id).catch(() => {});
    } catch {
      // Never let a prefetch throw into an event handler.
    }
  }

  function forRoute(id) {
    return load(id).catch((err) => {
      if (recoverFromChunkError(err, env)) {
        warn(`chunk "${id}" failed to load; reloading once for the current build`);
        return new Promise(() => {});
      }
      throw err;
    });
  }

  return { load, prefetch, forRoute, has: (id) => pending.has(id) };
}
